interface ApiErrorResponse {
  code?: string;
  message?: string;
  errors?: Record<string, string[]>;
}

type ApiClientOptions<TRequest extends object> = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: TRequest;
  body?: TRequest;
  customHeaders?: HeadersInit;
  /**
   * For server-side requests, pass the necessary headers
   * that you want to forward from the original request.
   */
  forwardedHeaders?: HeadersInit;
  tags?: string[];
  revalidate?: number;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Codice applicativo del backend, quando lo manda (es. `demo_slot_unavailable`) */
    public code?: string,
    /** Errori di validazione per campo, come li restituisce Laravel su 422 */
    public errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiClient<TRequest extends object, TResponse>(
  endpoint: string,
  options: ApiClientOptions<TRequest> = {}
): Promise<TResponse> {
  const {
    method = 'GET',
    query,
    body,
    customHeaders = {},
    forwardedHeaders = {}, // Capture the forwarded headers
    tags,
    revalidate,
  } = options;

  const url = new URL(`${process.env.NEXT_PUBLIC_API_BASE_URL}${endpoint}`);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      // Skip appending parameters that are null or undefined
      if (value !== null && value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  const mergedHeaders = new Headers({
    ...defaultHeaders,
    ...forwardedHeaders, // Merge the forwarded headers
    ...customHeaders,
  });

  const fetchOptions: RequestInit = {
    method,
    headers: mergedHeaders,
    next: {
      tags,
      revalidate,
    },
  };

  if (
    body &&
    typeof body === 'object' &&
    !(body instanceof Blob) &&
    !(body instanceof FormData) &&
    !(body instanceof URLSearchParams)
  ) {
    fetchOptions.body = JSON.stringify(body);
  } else if (body) {
    fetchOptions.body = body as BodyInit;
  }

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const errorData: ApiErrorResponse = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || 'An unexpected error occurred.';
    throw new ApiError(response.status, errorMessage, errorData.code, errorData.errors);
  }

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return null as TResponse;
  }

  return response.json() as Promise<TResponse>;
}
